import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Polygon } from "react-leaflet";
import { Plus, Undo2 } from "lucide-react";
import { PageContainer } from "@/layouts/PageContainer";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { FormField } from "@/components/ui/FormField";
import { Modal } from "@/components/ui/Modal";
import { Map } from "@/components/maps/Map";
import { Badge } from "@/components/ui/Badge";
import { LoadingState } from "@/components/feedback/LoadingState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ConfirmDialog } from "@/components/modals/ConfirmDialog";
import { useCreateZone, useDeleteZone, useSetZoneActive, useUpdateZone, useZonesList } from "@/features/zones/useZones";
import { usePricingRules } from "@/features/pricing/usePricing";
import { toast } from "@/store/toast.store";
import { cn } from "@/utils/cn";
import type { Zone } from "@/types/domain";

const DEFAULT_CENTER: [number, number] = [11.2588, 75.7804];

const schema = z.object({
  name: z.string().min(2, "Name is required"),
  pricingRuleKey: z.string().min(1),
});
type FormValues = z.infer<typeof schema>;

export default function Zones() {
  const zones = useZonesList();
  const rules = usePricingRules();
  const [selected, setSelected] = useState<Zone | null>(null);
  const [creating, setCreating] = useState(false);
  const [drawPoints, setDrawPoints] = useState<[number, number][]>([]);
  const [deleting, setDeleting] = useState<Zone | null>(null);

  const createZone = useCreateZone();
  const setZoneActive = useSetZoneActive();
  const updateZone = useUpdateZone();
  const deleteZone = useDeleteZone();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { pricingRuleKey: "default" } });

  const activeZone = selected ?? zones.data?.[0] ?? null;

  return (
    <PageContainer
      title="Service zones"
      description="Zones and their pricing/service status."
      actions={
        <Button
          size="sm"
          onClick={() => {
            reset({ name: "", pricingRuleKey: "default" });
            setDrawPoints([]);
            setCreating(true);
          }}
        >
          <Plus className="h-4 w-4" /> New zone
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
        <div className="card">
          {zones.isLoading && <LoadingState label="Loading zones…" />}
          {zones.error && <ErrorState error={zones.error} onRetry={() => zones.refetch()} />}
          {zones.data?.length === 0 && <EmptyState title="No zones configured yet" />}
          <ul>
            {zones.data?.map((zone) => (
              <li key={zone.id}>
                <button
                  onClick={() => setSelected(zone)}
                  className={cn(
                    "flex w-full items-center justify-between border-b border-border/60 px-4 py-3 text-left hover:bg-cream-200/60",
                    activeZone?.id === zone.id && "bg-cream-200",
                  )}
                >
                  <span className="text-sm font-medium text-ink">{zone.name}</span>
                  <Badge tone={zone.active ? "teal" : "neutral"}>
                    {zone.active ? "Active" : "Inactive"}
                  </Badge>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-4">
          <div className="card overflow-hidden">
            <Map
              center={
                activeZone
                  ? [activeZone.center.coordinates[1], activeZone.center.coordinates[0]]
                  : DEFAULT_CENTER
              }
              zoom={12}
              height={340}
            >
              {activeZone?.polygon?.coordinates?.[0] && (
                <Polygon
                  positions={activeZone.polygon.coordinates[0].map(([lng, lat]: number[]) => [lat, lng] as [number, number])}
                  pathOptions={{ color: "#0F6B5C", fillOpacity: 0.15 }}
                />
              )}
            </Map>
          </div>

          {activeZone && (
            <div className="card flex items-center justify-between p-4">
              <div>
                <p className="font-semibold text-ink">{activeZone.name}</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs text-ink-faint">Pricing rule:</span>
                  <Select
                    className="h-8 w-44 text-xs"
                    value={activeZone.pricingRuleKey}
                    disabled={updateZone.isPending}
                    onChange={(e) => {
                      const pricingRuleKey = e.target.value;
                      updateZone.mutate(
                        { id: activeZone.id, payload: { pricingRuleKey } },
                        {
                          onSuccess: () => {
                            toast.success("Zone pricing rule updated");
                            setSelected({ ...activeZone, pricingRuleKey });
                          },
                        },
                      );
                    }}
                  >
                    {rules.data?.map((r) => (
                      <option key={r.key} value={r.key}>
                        {r.name ?? r.key}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  loading={setZoneActive.isPending}
                  onClick={() =>
                    setZoneActive.mutate(
                      { id: activeZone.id, active: !activeZone.active },
                      {
                        onSuccess: () =>
                          toast.success(activeZone.active ? "Zone disabled" : "Zone enabled"),
                      },
                    )
                  }
                >
                  {activeZone.active ? "Disable" : "Enable"}
                </Button>
                <Button variant="danger" size="sm" onClick={() => setDeleting(activeZone)}>
                  Delete
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New zone"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              form="new-zone-form"
              type="submit"
              variant="primary"
              loading={createZone.isPending}
              disabled={drawPoints.length < 3}
            >
              Create
            </Button>
          </>
        }
      >
        <form
          id="new-zone-form"
          className="flex flex-col gap-3"
          onSubmit={handleSubmit((values) => {
            if (drawPoints.length < 3) return;
            createZone.mutate(
              {
                name: values.name,
                pricingRuleKey: values.pricingRuleKey,
                ring: drawPoints.map(([lat, lng]) => [lng, lat]),
                active: true,
              },
              {
                onSuccess: () => {
                  toast.success("Zone created");
                  setCreating(false);
                },
              },
            );
          })}
        >
          <FormField label="Zone name" error={errors.name?.message}>
            <Input placeholder="e.g. Calicut city" {...register("name")} />
          </FormField>

          <FormField
            label="Pricing rule"
            error={errors.pricingRuleKey?.message}
            hint="Fare formula applied to rides picked up inside this zone"
          >
            <Select {...register("pricingRuleKey")}>
              {rules.data?.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.name ?? r.key}
                </option>
              ))}
            </Select>
          </FormField>

          <div className="flex items-center justify-between">
            <p className="text-xs text-ink-faint">
              Click the map to add each corner of the service area ({drawPoints.length} point
              {drawPoints.length === 1 ? "" : "s"}, minimum 3).
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={drawPoints.length === 0}
              onClick={() => setDrawPoints((p) => p.slice(0, -1))}
            >
              <Undo2 className="h-3.5 w-3.5" /> Undo point
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border border-border">
            <Map
              center={DEFAULT_CENTER}
              zoom={12}
              height={320}
              onMapClick={(lat, lng) => setDrawPoints((p) => [...p, [lat, lng]])}
            >
              {drawPoints.length >= 2 && (
                <Polygon positions={drawPoints} pathOptions={{ color: "#D9A125", fillOpacity: 0.2 }} />
              )}
            </Map>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title={`Delete ${deleting?.name ?? "zone"}?`}
        description="This cannot be undone. Existing pricing tied to this zone will need to be reassigned."
        confirmLabel="Delete"
        loading={deleteZone.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          deleteZone.mutate(deleting.id, {
            onSuccess: () => {
              toast.success("Zone deleted");
              if (selected?.id === deleting.id) setSelected(null);
              setDeleting(null);
            },
          });
        }}
      />
    </PageContainer>
  );
}
